"use client";

import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from "modules/common/components/Table/TableComponents";
import { cn } from "utils/utils";
import { useViewPreference } from "modules/common/stores/useCommonStore";
import { TradePreviewCardSkeleton } from "modules/common/components/skeletons/trade/TradePreviewCardSkeleton";
import { ViewIndex } from "modules/common/model/viewTypes";

interface TradeListTableSkeletonProps {
  rowCount?: number;
  containerClassName?: string;
  isTradeList?: boolean;
  isMatchedTrades?: boolean;
  showUpdated?: boolean;
  isOwner?: boolean;
}

export function TradeListTableSkeleton({
  rowCount = 8,
  containerClassName = "relative max-h-[calc(100svh-195px)] md:max-h-[75vh] overflow-y-auto",
  isTradeList = true,
  showUpdated = true,
  isMatchedTrades = false,
  isOwner = false,
}: TradeListTableSkeletonProps) {
  return (
    <TableContainer
      className={cn(
        "rounded-box border-base-content/20 bg-base-100 shadow-xs border",
        containerClassName
      )}
    >
      <Table className="table-zebra w-full">
        <TableHead className="border-base-300/30 bg-base-100 sticky top-0 z-30 border-b">
          <TableRow className="border-none">
            {/* Select */}
            {isOwner && (
              <TableHeader className="bg-base-100 !pr-0 w-4 border-none" />
            )}
            {/* Qty */}
            <TableHeader className="bg-base-100 text-base-content/70 border-none text-sm font-medium">
              <div className="text-center">Qty</div>
            </TableHeader>
            {/* Name */}
            <TableHeader className="bg-base-100 text-base-content/70 border-none text-sm font-medium">
              Card
            </TableHeader>
            {/* Trade Type */}
            {isMatchedTrades && (
              <TableHeader className="bg-base-100 text-base-content/70 border-none text-sm font-medium">
                <div className="text-center">Type</div>
              </TableHeader>
            )}
            {/* Set */}
            <TableHeader className="bg-base-100 text-base-content/70 border-none text-sm font-medium">
              <div className="text-center">Set</div>
            </TableHeader>
            {/* Price */}
            <TableHeader className="bg-base-100 text-base-content/70 border-none text-sm font-medium">
              <div className="text-center">Price</div>
            </TableHeader>
            {/* Lang */}
            <TableHeader className="bg-base-100 text-base-content/70 border-none text-sm font-medium">
              <div className="text-center">Lang</div>
            </TableHeader>
            {/* Cond */}
            <TableHeader className="bg-base-100 text-base-content/70 border-none text-sm font-medium">
              <div className="text-center">Cond</div>
            </TableHeader>

            {/* Added */}
            {/* Updated */}
            {/* Actions */}

            {isTradeList ? (
              <>
                <TableHeader className="bg-base-100 text-base-content/70 border-none text-sm font-medium">
                  Added
                </TableHeader>

                <TableHeader className="bg-base-100 text-base-content/70 border-none text-sm font-medium">
                  Updated
                </TableHeader>

                <TableHeader className="bg-base-100 text-base-content/70 border-none text-sm font-medium">
                  <div className="text-center">Actions</div>
                </TableHeader>
              </>
            ) : (
              <>
                {showUpdated && (
                  <TableHeader className="bg-base-100 text-base-content/70 border-none text-sm font-medium">
                    {/* Contact */}
                    <div className="text-center">Updated</div>
                  </TableHeader>
                )}
                <TableHeader className="bg-base-100 text-base-content/70 border-none text-sm font-medium">
                  {/* Contact */}
                  <div className="text-center">User</div>
                </TableHeader>
                <TableHeader className="bg-base-100 text-base-content/70 border-none text-sm font-medium">
                  {/* Contact */}
                  <div className="text-center">Contact</div>
                </TableHeader>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: rowCount }).map((_, i) => (
            <TableRow
              key={i}
              className={cn(
                "border-base-300/20 border-b transition-all duration-150"
              )}
            >
              {/* Select */}
              {isOwner && (
                <TableCell className="text-base-content !pr-0 w-4 border-none py-2" />
              )}
              {/* Qty */}
              <TableCell className="text-base-content border-none px-3 py-2">
                <div className="skeleton mx-auto h-4 w-4" />
              </TableCell>
              {/* Name */}
              <TableCell className="py-4.5 text-base-content border-none px-3">
                <div className="skeleton h-5 w-28" />
              </TableCell>
              {/* Trade Type */}
              {isMatchedTrades && (
                <TableCell className="text-base-content border-none px-3 py-2">
                  <div className="skeleton mx-auto size-8 rounded-full" />
                </TableCell>
              )}
              {/* Set */}
              <TableCell className="text-base-content border-none px-3 py-2">
                <div className="skeleton mx-auto size-8 rounded-full" />
              </TableCell>
              {/* Price */}
              <TableCell className="text-base-content border-none px-3 py-2">
                <div className="skeleton mx-auto h-4 w-16" />
              </TableCell>
              {/* Lang */}
              <TableCell className="text-base-content border-none px-3 py-2">
                <div className="skeleton mx-auto h-5 w-7 rounded-md" />
              </TableCell>
              {/* Cond */}
              <TableCell className="text-base-content border-none px-3 py-2">
                <div className="skeleton mx-auto h-5 w-10 rounded-md" />
              </TableCell>

              {isTradeList ? (
                <>
                  {/* Added */}
                  <TableCell className="text-base-content border-none px-3 py-2">
                    <div className="skeleton h-4 w-10" />
                  </TableCell>

                  <TableCell className="text-base-content border-none px-3 py-2">
                    <div className="skeleton h-4 w-10" />
                  </TableCell>

                  {/* Actions */}
                  <TableCell className="text-base-content border-none px-3 py-2">
                    <div className="flex justify-center gap-1">
                      <div className="skeleton size-6 rounded-full" />
                    </div>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell className="text-base-content border-none px-3 py-2">
                    <div className="skeleton mx-auto size-6 rounded-full" />
                  </TableCell>
                  {showUpdated && (
                    <TableCell className="text-base-content border-none px-3 py-2">
                      <div className="flex justify-center gap-1">
                        <div className="skeleton size-6 rounded-full" />
                      </div>
                    </TableCell>
                  )}
                  <TableCell className="text-base-content border-none px-3 py-2">
                    <div className="flex justify-center gap-1">
                      <div className="skeleton size-6 rounded-full" />
                    </div>
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Visual-view skeleton: responsive grid of trade card skeletons.
 * On the owner-on-own-list view the cell collapses to just the card image
 * (no footer, no button) — mirror that by hiding everything but the image
 * + chip in the skeleton. */
function VisualSkeleton({
  count,
  isOwner,
}: {
  count: number;
  isOwner: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-2 p-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7">
      {Array.from({ length: count }).map((_, i) => (
        <TradePreviewCardSkeleton
          key={i}
          width="100%"
          hideUser={isOwner}
          hideTime={isOwner}
          hideAction={isOwner}
        />
      ))}
    </div>
  );
}

/**
 * View-preference-aware trade list skeleton.
 * initialViewIndex (from SSR cookie) is used when provided so the correct
 * skeleton renders immediately without waiting for Zustand hydration.
 */
export function TradeListSkeleton({
  rowCount = 10,
  initialViewIndex,
  isOwner = false,
}: {
  rowCount?: number;
  initialViewIndex?: ViewIndex;
  /** Owner-on-own-list collapses the cell to just the card; skeleton mirrors. */
  isOwner?: boolean;
}) {
  const { viewPreference } = useViewPreference();
  const isVisualView = (initialViewIndex ?? viewPreference) === 1;

  if (isVisualView) {
    return <VisualSkeleton count={rowCount} isOwner={isOwner} />;
  }

  return <TradeListTableSkeleton rowCount={rowCount} isOwner={isOwner} />;
}
